import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { parseMatchLog } from "../../lib/parseMatchLogClient";
import { diffRoster } from "../../lib/rosterValidationClient";
import { uploadToS3 } from "../../services/uploadToS3";
import { showModal } from "../statusModal/statusModal";

/**
 * Presign -> PUT file to S3 -> call /results/ingest
 */
export const uploadAndIngestResultLog = createAsyncThunk(
  "resultIngest/uploadAndIngest",
  async (
    { tournamentId, tableId, file, threshold = 10 },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      // 0) read file locally & parse
      const text = await file.text();
      const teams = parseMatchLog(text);

      // 1) get roster (from state or API fallback)
      let tablePlayers = getState().players?.data?.[tableId];
      if (!Array.isArray(tablePlayers) || tablePlayers.length === 0) {
        // fallback API to be safe when the page is cold-loaded
        const { data } = await axiosInstance.get(
          `/tables/${tableId}/roster-ids`,
          {
            params: { tournamentId },
          }
        );
        tablePlayers = (data?.ids || []).map((id) => ({ gameId: id }));
      }

      const tablePlayerGameIds = [
        ...new Set(
          (tablePlayers || []).map((p) => String(p.gameId || "").trim())
        ),
      ];

      const diff = diffRoster(teams, tablePlayerGameIds); // expect { count, notInRoster?, sample? }

      if (diff.count > threshold) {
        // optional: show a modal for UX
        dispatch(
          showModal({
            status: "error",
            message: `الملف يحتوي ${diff.count} لاعب غير مسجّل في هذا الجدول.`,
          })
        );
        // IMPORTANT: reject so UI knows this didn’t proceed
        return rejectWithValue({
          code: "ROSTER_MISMATCH",
          count: diff.count,
          sample: diff.sample?.slice(0, 10) || [],
        });
      }

      // 2) presign
      const { data: presign } = await axiosInstance.post(
        "resultLogs/results/uploads/presign",
        { tournamentId, tableId }
      );
      const { url, key } = presign;

      // 3) upload to S3 (make sure your helper sets the same Content-Type you used in presign)
      await uploadToS3({
        url,
        file,
        onProgress: (pct) => dispatch(setUploadProgress({ tableId, pct })),
      });

      // 4) notify backend to ingest
      const { data } = await axiosInstance.post("resultLogs/results/ingest", {
        tournamentId,
        tableId,
        s3Key: key,
      });

      return { tableId, ingestId: data.ingestId };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: String(err) });
    } finally {
      // always clear visual progress in the slice
      dispatch(setUploadProgress({ tableId, pct: undefined }));
    }
  }
);

export const fetchTableIngestStatus = createAsyncThunk(
  "resultIngest/fetchStatus",
  async ({ tableId }) => {
    const { data } = await axiosInstance.get("resultLogs/results/status", {
      params: { tableId },
    });
    return { tableId, latest: data.latest || null };
  }
);

export const retryIngest = createAsyncThunk(
  "resultIngest/retry",
  async ({ ingestId }) => {
    const { data } = await axiosInstance.post("resultLogs/results/retry", {
      ingestId,
    });
    return { ingestId, status: data.status };
  }
);

export const deleteIngest = createAsyncThunk(
  "resultIngest/deleteIngest",
  async ({ ingestId, force = false }) => {
    const { data } = await axiosInstance.delete(
      `resultLogs/results/ingest/${ingestId}`,
      {
        params: { force },
      }
    );
    return { ingestId, force, ok: data.ok };
  }
);

const slice = createSlice({
  name: "resultIngest",
  initialState: {
    byTable: {}, // { [tableId]: { latest, uploadingPct } }
    lastIngestId: null,
    error: null,
  },
  reducers: {
    setUploadProgress(state, { payload }) {
      const { tableId, pct } = payload;
      if (!state.byTable[tableId]) state.byTable[tableId] = {};
      state.byTable[tableId].uploadingPct = pct;
    },
    clearUploadProgress(state, { payload }) {
      const { tableId } = payload;
      if (state.byTable[tableId]) delete state.byTable[tableId].uploadingPct;
    },
  },
  extraReducers: (b) => {
    b.addCase(uploadAndIngestResultLog.pending, (s, a) => {
      const { tableId } = a.meta.arg;
      if (!s.byTable[tableId]) s.byTable[tableId] = {};
      s.byTable[tableId].uploadingPct = 0;
      s.error = null;
    })
      .addCase(uploadAndIngestResultLog.fulfilled, (s, a) => {
        const { tableId, ingestId } = a.payload;
        s.lastIngestId = ingestId;
        if (!s.byTable[tableId]) s.byTable[tableId] = {};
        s.byTable[tableId].uploadingPct = undefined; // hide progress
      })
      .addCase(uploadAndIngestResultLog.rejected, (s, a) => {
        s.error = a.payload?.message || "Upload/ingest failed";
      })
      .addCase(fetchTableIngestStatus.fulfilled, (s, a) => {
        const { tableId, latest } = a.payload;
        s.byTable[tableId] = { ...(s.byTable[tableId] || {}), latest };
      })
      .addCase(retryIngest.fulfilled, (s, a) => {
        // optimistic: mark as queued (UI badge will show parsing…)
        const ingestId = a.payload.ingestId;
        for (const tId of Object.keys(s.byTable)) {
          const latest = s.byTable[tId]?.latest;
          if (latest && latest._id === ingestId) {
            s.byTable[tId].latest.status = "queued";
          }
        }
      })
      .addCase(deleteIngest.fulfilled, (s, a) => {
        const { ingestId } = a.payload;
        // clear any table that was pointing at this ingest
        for (const tId of Object.keys(s.byTable)) {
          const latest = s.byTable[tId]?.latest;
          if (latest && latest._id === ingestId) {
            s.byTable[tId].latest = null;
            s.byTable[tId].uploadingPct = undefined;
          }
        }
      });
  },
});

export const { setUploadProgress, clearUploadProgress } = slice.actions;
export default slice.reducer;
